import { TransactionReceipt, TransactionRequest } from 'ethers'
import { get } from 'lodash'
import { ChevronDownIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import {
  SubmittedTransactionResponse,
  TransactionConfirmationRequest,
  TransactionRequestStep,
  TransactionStatus,
} from '@/contexts/TransactionProvider'
import { useWeb3Context } from '@/contexts/Web3Provider'
import { IconNames } from '@/enums'
import { ErrorHandler } from '@/helpers'
import { tryCatch } from '@/helpers/try-catch'
import { cn } from '@/theme/utils'
import { UiButton } from '@/ui/UiButton'
import { UiButtonGradient } from '@/ui/UiButtonGradient'
import {
  UiCollapsible,
  UiCollapsibleContent,
  UiCollapsibleTrigger,
} from '@/ui/UiCollapsible'
import UiIcon from '@/ui/UiIcon'
import { UiSeparator } from '@/ui/UiSeparator'

import TransactionStatusIndicator from './TransactionStatusIndicator'

export interface TransactionConfirmationModalProps {
  isOpen: boolean
  txDetails: TransactionConfirmationRequest

  handleCancel: () => void
  handleDismiss: (errors?: Error[]) => void
  handleSuccess: (txReceipts: TransactionReceipt[]) => void
}

/**
 * A reusable modal component for transaction confirmation flow
 * Handles waiting for confirmation, processing, success, and error states
 */
export default function TransactionConfirmationModal({
  isOpen,

  txDetails,

  handleCancel,
  handleDismiss,
  handleSuccess,
}: TransactionConfirmationModalProps) {
  const isSingleTransaction = txDetails.steps.length === 1

  if (isSingleTransaction) {
    return (
      <SingleTransaction
        isOpen={isOpen}
        requestStep={txDetails.steps[0]}
        handleCancel={handleCancel}
        handleDismiss={handleDismiss}
        handleSuccess={handleSuccess}
      />
    )
  }

  return (
    <MultipleTransactions
      isOpen={isOpen}
      requestSteps={txDetails.steps}
      handleCancel={handleCancel}
      handleDismiss={handleDismiss}
      handleSuccess={handleSuccess}
    />
  )
}

const SingleTransaction = ({
  isOpen,
  requestStep,

  handleCancel,
  handleDismiss,
  handleSuccess,
}: {
  requestStep: TransactionRequestStep
} & Omit<TransactionConfirmationModalProps, 'txDetails'>) => {
  const { signAndSendTransaction } = useWeb3Context()

  const [isDetailsShown, setIsDetailsShown] = useState(false)

  const [status, setStatus] = useState<TransactionStatus>('pending')
  const [txHash, setTxHash] = useState<string>()

  const [error, setError] = useState<Error>()
  const [txReceipt, setTxReceipt] = useState<TransactionReceipt>()

  const handleConfirm = useCallback(async () => {
    setStatus('processing')

    const [receipt, error] = await tryCatch(
      (async () => {
        if ((requestStep.txRequest as TransactionRequest).to) {
          return await signAndSendTransaction(
            requestStep.txRequest as TransactionRequest,
            {
              onTxSent: async tx => {
                setTxHash(tx.hash)
              },
            },
          )
        }

        return await (
          requestStep.txRequest as () => Promise<TransactionReceipt>
        )()
      })(),
    )
    if (error) {
      ErrorHandler.processWithoutFeedback(error)
      setError(error as Error)
      setStatus('failed')
      return
    }

    setTxHash(receipt.hash)
    setStatus('success')
    setTxReceipt(receipt)
  }, [requestStep, signAndSendTransaction])

  return (
    <ResponsiveModalDrawer
      open={isOpen}
      title={renderTitle(status)}
      description={renderDescription(status)}
      content={
        <div className='sm:max-w-md'>
          <div className='py-4'>
            {/* Transaction summary - always visible */}
            <div className='mb-4 flex flex-col items-center rounded-lg border p-4'>
              <h4 className='text-foreground typography-m3-title-large mb-2 font-medium'>
                {requestStep.title}
              </h4>
              {requestStep.description && (
                <p className='text-muted-foreground typography-m3-title-small mb-3'>
                  {requestStep.description}
                </p>
              )}

              {requestStep.fromToken && requestStep.toToken && (
                <div className='w-full'>
                  <RequestWithTokens requestStep={requestStep} />
                </div>
              )}

              {requestStep.metadata && (
                <div className='w-full'>
                  <UiCollapsible
                    open={isDetailsShown}
                    onOpenChange={setIsDetailsShown}
                  >
                    <UiCollapsibleTrigger asChild>
                      <button
                        onClick={() => setIsDetailsShown(!isDetailsShown)}
                        className='text-primary w-full self-center text-center text-sm hover:underline'
                      >
                        {isDetailsShown ? 'Hide details' : 'Show details'}

                        <ChevronDownIcon
                          className={cn(
                            'ml-1 inline size-4',
                            isDetailsShown && 'rotate-180',
                          )}
                        />
                      </button>
                    </UiCollapsibleTrigger>
                    <UiCollapsibleContent className='mt-3'>
                      <RequestMetadataContent requestStep={requestStep} />
                    </UiCollapsibleContent>
                  </UiCollapsible>
                </div>
              )}
            </div>

            {/* Status indicator when processing, success, or failed */}
            {status !== 'pending' && (
              <TransactionStatusIndicator status={status} hash={txHash} />
            )}
          </div>
        </div>
      }
      footer={(() => {
        if (status === 'pending') {
          return (
            <div className='flex flex-1 items-center gap-2'>
              <UiButtonGradient
                variant='ghost'
                onClick={handleCancel}
                radius='full'
                className='bg-card text-foreground w-full flex-1 sm:w-auto'
              >
                Cancel
              </UiButtonGradient>
              <UiButtonGradient
                onClick={handleConfirm}
                className='w-full flex-1 sm:w-auto'
              >
                Confirm Transaction
              </UiButtonGradient>
            </div>
          )
        }

        if (status === 'processing') {
          return (
            <UiButton variant='outline' disabled className='w-full'>
              Processing...
            </UiButton>
          )
        }

        if (status === 'failed' || !txReceipt) {
          return (
            <>
              <UiButton
                variant='destructive'
                onClick={() => handleDismiss(error ? [error] : [])}
                className='w-full'
              >
                Close
              </UiButton>
              <UiButton onClick={handleConfirm} className='w-full'>
                Retry
              </UiButton>
            </>
          )
        }

        return (
          <UiButton
            variant='default'
            onClick={() => handleSuccess([txReceipt])}
            className='w-full'
          >
            Done
          </UiButton>
        )
      })()}
    />
  )
}

function MultipleTransactions({
  isOpen,
  requestSteps,

  handleCancel,
  handleDismiss,
  handleSuccess,
}: {
  requestSteps: TransactionRequestStep[]
} & Omit<TransactionConfirmationModalProps, 'txDetails'>) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [currentStep, setCurrentStep] = useState(-1)
  const [attempts, setAttempts] = useState(0)

  const [submittedTransactionResponses, setSubmittedTransactionResponses] =
    useState<(SubmittedTransactionResponse | undefined)[]>([])

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true)
    setCurrentStep(0)
  }, [])

  return (
    <ResponsiveModalDrawer
      open={isOpen}
      title='Confirm Transactions'
      description='Please review the transaction details before confirming'
      content={
        <div className='flex flex-col gap-4 p-4'>
          {requestSteps.map((requestStep, index) => (
            <div key={index} className='flex flex-col gap-4'>
              <MultipleTransactionsItem
                response={submittedTransactionResponses[index]}
                isCanStart={index === currentStep}
                requestStep={requestStep}
                key={`${index}-${attempts}`}
                onSuccess={txReceipt => {
                  setSubmittedTransactionResponses(prev => {
                    const newResponses = [...prev]
                    newResponses[index] = txReceipt
                    return newResponses
                  })

                  setCurrentStep(prev => prev + 1)

                  if (index === requestSteps.length - 1) {
                    setIsSubmitting(false)
                  }
                }}
                onError={error => {
                  setSubmittedTransactionResponses(prev => {
                    const newResponses = [...prev]
                    newResponses[index] = error
                    return newResponses
                  })

                  setIsSubmitting(false)
                }}
              />

              {index < requestSteps.length - 1 && (
                <UiSeparator key={`separator-${index}`} />
              )}
            </div>
          ))}
        </div>
      }
      footer={(() => {
        if (isSubmitting) {
          return (
            <UiButton variant='outline' disabled className='w-full'>
              Processing...
            </UiButton>
          )
        }

        if (!submittedTransactionResponses.length) {
          return (
            <div className='flex flex-1 items-center gap-2'>
              <UiButtonGradient
                variant='ghost'
                onClick={handleCancel}
                radius='full'
                className='bg-card text-foreground w-full flex-1 sm:w-auto'
              >
                Cancel
              </UiButtonGradient>
              <UiButtonGradient
                onClick={handleConfirm}
                className='w-full flex-1 sm:w-auto'
              >
                Confirm Transaction
              </UiButtonGradient>
            </div>
          )
        }

        if (submittedTransactionResponses.some(tx => tx instanceof Error)) {
          return (
            <>
              <UiButton
                variant='destructive'
                onClick={() =>
                  handleDismiss(
                    submittedTransactionResponses.filter(
                      tx => tx instanceof Error,
                    ),
                  )
                }
                className='w-full'
              >
                Close
              </UiButton>
              <UiButton
                onClick={() => {
                  setAttempts(prev => prev + 1)
                  setIsSubmitting(true)
                  setSubmittedTransactionResponses(prev => {
                    const newResponses = [...prev]
                    newResponses[currentStep] = undefined
                    return newResponses
                  })
                }}
                className='w-full'
              >
                Retry
              </UiButton>
            </>
          )
        }

        return (
          <UiButton
            variant='default'
            onClick={() =>
              handleSuccess(
                submittedTransactionResponses as TransactionReceipt[],
              )
            }
            className='w-full'
          >
            Done
          </UiButton>
        )
      })()}
    />
  )
}

function MultipleTransactionsItem({
  response,
  isCanStart,
  requestStep,

  onSuccess,
  onError,
}: {
  response: SubmittedTransactionResponse | undefined
  isCanStart: boolean
  requestStep: TransactionRequestStep
  onSuccess: (txReceipt: TransactionReceipt) => void
  onError: (error: Error) => void
}) {
  const { signAndSendTransaction } = useWeb3Context()

  const [isDetailsShown, setIsDetailsShown] = useState(false)

  const [_status, setStatus] = useState<TransactionStatus>('pending')
  const status =
    response instanceof Error
      ? 'failed'
      : get(response, 'hash', undefined)
        ? 'success'
        : _status

  const [txHash, setTxHash] = useState<string | undefined>(
    get(response, 'hash', undefined),
  )

  const isStarted = useRef(false)

  const start = useCallback(async () => {
    setStatus('processing')

    const [receipt, error] = await tryCatch(
      (async () => {
        if ((requestStep.txRequest as TransactionRequest).to) {
          return await signAndSendTransaction(
            requestStep.txRequest as TransactionRequest,
            {
              onTxSent: async tx => {
                setTxHash(tx.hash)
              },
            },
          )
        }

        return await (
          requestStep.txRequest as () => Promise<TransactionReceipt>
        )()
      })(),
    )
    if (error) {
      ErrorHandler.processWithoutFeedback(error)
      setStatus('failed')
      onError(error as Error)
      return
    }

    setTxHash(receipt.hash)
    setStatus('success')
    onSuccess(receipt)
  }, [onError, onSuccess, requestStep.txRequest, signAndSendTransaction])

  useEffect(() => {
    if (isStarted.current || !isCanStart) return

    start()
    isStarted.current = true
  }, [isCanStart, start])

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex flex-row justify-between gap-4'>
        <div className='flex flex-col'>
          <h4 className='text-foreground font-medium'>{requestStep.title}</h4>
          {requestStep.description && (
            <p className='text-muted-foreground text-sm'>
              {requestStep.description}
            </p>
          )}
        </div>
        <TransactionStatusIndicator
          className='self-center'
          compact
          status={status}
        />
      </div>

      <UiCollapsible open={isDetailsShown} onOpenChange={setIsDetailsShown}>
        <UiCollapsibleTrigger asChild>
          <button
            onClick={() => setIsDetailsShown(!isDetailsShown)}
            className='text-primary text-sm hover:underline'
          >
            {isDetailsShown ? 'Hide details' : 'Show details'}
          </button>
        </UiCollapsibleTrigger>
        <UiCollapsibleContent className='mt-3'>
          {/* Status indicator when processing, success, or failed */}
          {status !== 'pending' && (
            <TransactionStatusIndicator status={status} hash={txHash} />
          )}

          {requestStep.fromToken && requestStep.toToken && (
            <RequestWithTokens requestStep={requestStep} />
          )}

          {requestStep.metadata && (
            <RequestMetadataContent requestStep={requestStep} />
          )}
        </UiCollapsibleContent>
      </UiCollapsible>
    </div>
  )
}

function RequestWithTokens({
  requestStep,
}: {
  requestStep: TransactionRequestStep
}) {
  if (!requestStep.fromToken || !requestStep.toToken) {
    return null
  }

  return (
    <div className='my-3 flex items-center gap-2'>
      <div className='flex-1'>
        <div className='text-muted-foreground typography-m3-body-small mb-2 uppercase'>
          Sell
        </div>
        <div className='bg-card border-border text-foreground flex items-center justify-between rounded-md border-1 px-4 py-4 font-medium'>
          <div className='flex items-center gap-1'>
            {(() => {
              if (requestStep.fromToken.iconUrl) {
                return (
                  <img
                    src={requestStep.fromToken.iconUrl}
                    alt={requestStep.fromToken.symbol}
                    className='aspect-square size-6 rounded-full'
                  />
                )
              }

              return <></>
            })()}
            <span className='text-foreground'>
              {requestStep.fromToken.amount}
            </span>
          </div>
          <span className='text-muted-foreground'>
            {requestStep.fromToken.symbol}
          </span>
        </div>
      </div>
      <UiIcon
        name={IconNames.ArrowDown}
        className='mx-2 aspect-square size-4 translate-y-3 rotate-270'
      />
      <div className='flex-1'>
        <div className='text-muted-foreground typography-m3-body-small mb-2 uppercase'>
          Buy
        </div>
        <div className='bg-card border-border text-foreground flex items-center justify-between rounded-md border-1 px-4 py-4 font-medium'>
          <div className='flex items-center gap-1'>
            {(() => {
              if (requestStep.toToken.iconUrl) {
                return (
                  <img
                    src={requestStep.toToken.iconUrl}
                    alt={requestStep.toToken.symbol}
                    className='h-6 w-6 rounded-full'
                  />
                )
              }

              return <></>
            })()}
            <span className='text-foreground'>
              {requestStep.toToken.amount}
            </span>
          </div>
          <span className='text-muted-foreground'>
            {requestStep.toToken.symbol}
          </span>
        </div>
      </div>
    </div>
  )
}

function RequestMetadataContent({
  requestStep,
}: {
  requestStep: TransactionRequestStep
}) {
  if (!requestStep.metadata) return null

  return (
    <div className='mt-3 w-full border-t pt-3'>
      {Object.entries(requestStep.metadata).map(([key, value], index) => (
        <div
          key={index}
          className='even:bg-card flex justify-between px-4 py-2 text-sm'
        >
          <span className='text-muted-foreground'>{key}</span>
          {(() => {
            if (typeof value === 'string') {
              return <span className='text-foreground'>{value}</span>
            }

            return value
          })()}
        </div>
      ))}
    </div>
  )
}

const renderTitle = (status: TransactionStatus): string => {
  switch (status) {
    case 'pending':
      return 'Confirm Transaction'
    case 'processing':
      return 'Transaction in Progress'
    case 'success':
      return 'Transaction Successful'
    case 'failed':
      return 'Transaction Failed'
  }
}

const renderDescription = (status: TransactionStatus): string => {
  switch (status) {
    case 'pending':
      return 'Please review the transaction details before confirming'
    case 'processing':
      return 'Your transaction is being processed. This may take a few moments.'
    case 'success':
      return 'Your transaction has been confirmed successfully.'
    case 'failed':
      return 'There was an error with your transaction. Please try again.'
  }
}
